import { useState, useEffect } from 'react';
import { replicateApi } from './replicateApi.js';
import { T } from '../../shared/styles/tokens.js';

// This page carried its own dark palette, out of step with the rest of the tool.
// Key names are kept so the ~30 call sites below read unchanged; `teal` is gone.
const C = {
  bg: T.paper, surface: T.surface, border: T.rule,
  text: T.text, muted: T.text3, dim: T.text2, ink: T.ink,
  danger: T.error, warn: T.warn, success: T.valid, mono: T.fontMono,
};

function Badge({ children, color = C.muted }) {
  return (
    <span className="o-badge" style={{ color, background: `${color}1A`, marginLeft: 6 }}>
      {children}
    </span>
  );
}

export default function ReplicatePage() {
  const [status, setStatus] = useState(null); // { configured } | null (loading)
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [copySchema, setCopySchema] = useState(false);
  const [copyData, setCopyData] = useState(true);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    replicateApi.getStatus()
      .then(s => {
        setStatus(s);
        if (!s.configured) { setLoading(false); return; }
        return replicateApi.listTables().then(d => setTables(d.tables || []));
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => setSelected(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const selectAll = () => setSelected(new Set(tables.map(t => `${t.schema}.${t.name}`)));
  const selectNone = () => setSelected(new Set());

  const run = async () => {
    if (selected.size === 0) { setErr('Select at least one table.'); return; }
    if (!copySchema && !copyData) { setErr('Enable Copy Schema and/or Copy Data.'); return; }
    setErr(null); setResults(null); setRunning(true);
    try {
      const d = await replicateApi.run([...selected], copySchema, copyData);
      setResults(d.results || []);
      // Refresh table list so schema-match indicators reflect the new state.
      const t = await replicateApi.listTables();
      setTables(t.tables || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: C.muted, fontSize: 13 }}>Loading…</div>;

  if (status && !status.configured) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>
        <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 10 }}>🔌</div>
        This feature isn't available in this environment.<br />
        It requires <code style={{ color: C.text }}>SOURCE_PostgreSQL_ConnectionString</code> to be set
        on this backend's App Settings, pointing at the source (production) database —
        normally configured only on the test environment.
      </div>
    );
  }

  return (
    <div style={{ padding: 20, fontFamily: 'inherit', color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Replicate from Production</div>
        <div style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={copySchema} onChange={e => setCopySchema(e.target.checked)}
            style={{ accentColor: C.ink }} />
          Copy Schema
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={copyData} onChange={e => setCopyData(e.target.checked)}
            style={{ accentColor: C.ink }} />
          Copy Data
        </label>
        <button onClick={run} disabled={running} style={{
          background: C.ink, color: '#fff', border: 'none', borderRadius: 8,
          padding: '7px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          opacity: running ? 0.6 : 1,
        }}>
          {running ? 'Running…' : `Run on ${selected.size} table${selected.size !== 1 ? 's' : ''}`}
        </button>
      </div>

      <div style={{ fontSize: 11, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>
        <b>Copy Data only:</b> requires the target to already have every source column — deletes
        existing rows and copies fresh data from production.<br />
        <b>Copy Schema:</b> creates missing tables and adds/drops/retypes columns so the target
        matches production's column structure exactly, then copies data if also enabled.<br />
        Select tables in dependency order (parents before children) if your schema has foreign keys.
      </div>

      {err && (
        <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}44`, borderRadius: 8,
          padding: '8px 12px', marginBottom: 12, fontSize: 12, color: C.danger }}>{err}</div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 12 }}>
        <button onClick={selectAll} style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>Select all</button>
        <button onClick={selectNone} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 0 }}>Clear</button>
      </div>

      <div className="o-panel" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, width: 30 }}></th>
              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted }}>Table</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted }}>Status</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted }}>Last run</th>
            </tr>
          </thead>
          <tbody>
            {tables.map(t => {
              const key = `${t.schema}.${t.name}`;
              const result = results?.find(r => r.table === key);
              return (
                <tr key={key} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '7px 12px' }}>
                    <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)}
                      style={{ accentColor: C.ink }} />
                  </td>
                  <td style={{ padding: '7px 12px', fontFamily: C.mono }}>{key}</td>
                  <td style={{ padding: '7px 12px' }}>
                    {!t.exists_in_target ? <Badge color={C.warn}>missing in target</Badge> :
                      t.schema_matches ? <Badge color={C.success}>schema matches</Badge> :
                      <Badge color={C.danger}>schema differs</Badge>}
                    {t.missing_in_target?.length > 0 && <Badge color={C.danger}>+{t.missing_in_target.length} col missing</Badge>}
                    {t.extra_in_target?.length > 0 && <Badge color={C.muted}>+{t.extra_in_target.length} col extra</Badge>}
                    {t.type_mismatches?.length > 0 && <Badge color={C.warn}>{t.type_mismatches.length} type diff</Badge>}
                  </td>
                  <td style={{ padding: '7px 12px' }}>
                    {result && (
                      result.error
                        ? <span style={{ color: C.danger }}>✗ {result.error}</span>
                        : <span style={{ color: C.success }}>✓ {result.action}{result.rows_copied ? `, ${result.rows_copied} rows` : ''}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {tables.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: C.muted }}>No tables found in source.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
